/******/ (() => { // webpackBootstrap
class CartQuantity extends HTMLElement {
  constructor() {
    super();
    this.removeButton = this.querySelector('[data-cart-remove-item]');
    this.adjustButtons = this.querySelectorAll('[data-cart-quantity-button]');
    this.input = this.querySelector('[data-cart-quantity-input]');
    this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleRemoveItem = this.handleRemoveItem.bind(this);
    this.updateCart = this.updateCart.bind(this);
    this.updateLineItem = this.updateLineItem.bind(this);
    this.updateCartCountIndicator = this.updateCartCountIndicator.bind(this);
    this.handleCartErrors = this.handleCartErrors.bind(this);
    this.setLoadingState = this.setLoadingState.bind(this);
    this.hideLoadingState = this.hideLoadingState.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-cart-quantity'});
    this.removeButton?.addEventListener('click', this.handleRemoveItem);
    this.adjustButtons.forEach(button => button.addEventListener('click', this.handleButtonClick));
    this.input?.addEventListener('change', this.handleInputChange);
  }

  handleButtonClick(e) {
    /* ===== Handle the button click event ===== */
    e.preventDefault();
    const previousValue = this.input.value;
    const adjustment = e.currentTarget.name === 'plus' ? 1 : -1;

    // Adjust the quantity by the adjustment value
    this.adjustQuantity(adjustment);

    // Process the new quantity if quantity has changed
    if (previousValue !== this.input.value) {
      this.handleQuantityChange(this.input.value);
    }
  }

  adjustQuantity(adjustment) {
    /* ===== Adjust the quantity by the adjustment value ===== */
    const newValue = parseInt(this.input.value) + adjustment;
    // If the new value is not a number, set it to 1
    this.input.value = isNaN(newValue) ? 1 : newValue;

    // If the value is smaller than the min attribute, set it to the min attribute
    if (this.input.min && parseInt(this.input.value) < parseInt(this.input.min)) {
      this.input.value = this.input.min;
    }
  }

  handleInputChange(e) {
    /* ===== Handle the change event ===== */
    e.preventDefault();

    // If the value is not a number, set it to 1
    if (isNaN(this.input.value)) {
      this.input.value = 1;
    }

    // If the value is smaller than the min attribute, set it to the min attribute
    if (this.input.min && parseInt(this.input.value) < parseInt(this.input.min)) {
      this.input.value = this.input.min;
    }

    // Process the new quantity
    this.handleQuantityChange(this.input.value);
  }

  handleQuantityChange(inputValue) {
    const quantity = parseInt(inputValue);
    const kitGroupId = this.getKitGroupId();
    if (kitGroupId) {
      this.updateCartWithKit(kitGroupId, quantity);
    } else {
      this.updateCart(this.itemIndex, quantity);
    }
  }

  async handleRemoveItem() {
    const kitGroupId = this.getKitGroupId();
    if (kitGroupId) {
      await this.removeWithKit(kitGroupId);
    } else {
      this.updateCart(this.itemIndex, 0);
    }
  }
  
  getKitGroupId() {
    const card = this.closest('.cart-drawer__product_card');
    if (!card) return null;
    const kitRow = card.querySelector('[data-kit-child]');
    return kitRow ? kitRow.dataset.kitGroupId || null : null;
  }
  
  async removeWithKit(kitGroupId) {
    try {
      this.setLoadingState();

      // Fetch cart to get line keys
      const cart = await this.getCart();
      if (!cart) { this.hideLoadingState(); return; }
      const kitItems = cart.items.filter(item => {
        const props = item.properties || {};
        return props._kit_is_child === 'true' && props._kit_group_id === kitGroupId;
      });

      // Build updates object — zero out the main line and every kit child by key
      const updates = {};
      // Main product: find by matching group id on parent
      const mainItem = cart.items.find(item => {
        const props = item.properties || {};
        return props._kit_group_id === kitGroupId && props._kit_is_child !== 'true';
      });

      if (mainItem) updates[mainItem.key] = 0;
      kitItems.forEach(kitItem => { updates[kitItem.key] = 0; });
  
      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: 'cart-drawer' })
      });
  
      const parsed = await response.json();
      if (parsed.errors) {
        this.handleCartErrors(this.itemIndex, parsed.errors);
        return;
      }
  
      window.eventBus.emit('update:cart:drawer', parsed);
      this.updateCartCountIndicator();
    } catch (error) {
      console.error('Error removing item with kit:', error);
    }
  }
  
  async updateCartWithKit(kitGroupId, newMainQty) {
    try {
      this.setLoadingState();

      const cart = await this.getCart();
      if (!cart) { this.hideLoadingState(); return; }

      const mainItem = cart.items.find(item => {
        const props = item.properties || {};
        return props._kit_group_id === kitGroupId && props._kit_is_child !== 'true';
      });
  
      const kitItems = cart.items.filter(item => {
        const props = item.properties || {};
        return props._kit_is_child === 'true' && props._kit_group_id === kitGroupId;
      });

      const updates = {};
      if (mainItem) updates[mainItem.key] = newMainQty;
      kitItems.forEach(kitItem => {
        const kitQtyPerUnit = parseInt(kitItem.properties._kit_parent_quantity) || 1;
        updates[kitItem.key] = kitQtyPerUnit * newMainQty;
      });
  
      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: 'cart-drawer' })
      });
  
      const parsed = await response.json();
      if (parsed.errors) {
        this.handleCartErrors(this.itemIndex, parsed.errors);
        return;
      }
  
      window.eventBus.emit('update:cart:drawer', parsed);
      this.updateCartCountIndicator();
    } catch (error) {
      console.error('Error updating cart with kit:', error);
    }
  }

  async updateCart(line, quantity) {
    try {
      /* ===== Set the loading state ===== */
      this.setLoadingState();

      /* ===== Update the line item quantity ===== */
      let response = await this.updateLineItem(line, quantity);
      if (!response) return;

      /* ===== If successful response and has gift wrap, update the gift wrap product quantity ===== */
      if (this.hasGiftWrap) {
        const adjustment = quantity - this.previousQuantity;
        const cart = await this.getCart();
        if (this.getGiftWrapLine(cart) && this.getGiftWrapQuantity(cart)) {
          response = await this.updateLineItem(this.getGiftWrapLine(cart), this.getGiftWrapQuantity(cart) + adjustment);
        }
      }
      if (!response) return;

      /* ===== If successful response, re-render the cart ===== */
      window.eventBus.emit('update:cart:drawer', response);

      /* ===== Update cart count ===== */
      this.updateCartCountIndicator();
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  async updateLineItem(line, quantity) {
    try {
      /* ===== Update line item quantity ===== */
      const body = JSON.stringify({
        line,
        quantity,
        sections: 'cart-drawer'
      });

      const response = await fetch(window.routes.cart_change_url, { 
        ...this.fetchConfig(),
        body
      });

      const responseText = await response.text();

      if (!responseText) {
        this.handleCartErrors();
        return null;
      }

      const parsedResponse = JSON.parse(responseText);

      /* ===== Handle errors ===== */
      if (parsedResponse.errors) {
        this.handleCartErrors(line, parsedResponse.errors);
        return null;
      }

      return parsedResponse;
    } catch (error) {
      console.error('Error updating cart line item:', error);
    }
  }

  fetchConfig(type = 'json') {
    /* ===== Create the fetch configuration object ===== */
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

  handleCartErrors(line, errors) {
    /* ===== Reset loading state and input value ===== */
    this.hideLoadingState();
    this.input.value = this.input.getAttribute('value');

    /* ===== Display the error message ===== */
    this.renderErrorMessage(line, errors);
  }

  renderErrorMessage(line, message) {
    /* ===== Display the error message ===== */
    const errorElement = document.querySelector(`[data-cart-error-index="${line}"]`);
    if (!errorElement) return;
    errorElement.innerHTML = message;
    errorElement.setAttribute('aria-hidden', false);

    /* ===== If we're at the bottom of the cart, scroll error message into view ===== */
    const isLastItem = errorElement.getAttribute('data-cart-is-last-item') === 'true';
    if (isLastItem) {
      errorElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  async getCart() {
    /* ===== Fetch the cart information ===== */
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error(`Error fetching cart: ${error.description || error.message}`);
    }
  }

  updateCartCountIndicator() {
    /* ===== Update the cart count indicator ===== */
    if (!this.cartCountIndicator) return;
    this.getCart().then(cart => window.wetheme.updateCartCount(cart));
  }

  getGiftWrapLine(cart) {
    return cart.items.findIndex(item => item.variant_id === this.giftWrapVariantId) + 1 || null;
  }

  getGiftWrapQuantity(cart) {
    return cart.items.find(item => item.variant_id === this.giftWrapVariantId)?.quantity || null;
  }

  setLoadingState() {
    this.classList.add('is-loading');
  }

  hideLoadingState() {
    this.classList.remove('is-loading');
  }

  get itemIndex() {
    return parseInt(this.getAttribute('data-item-index'));
  }

  get previousQuantity() {
    return parseInt(this.getAttribute('data-item-quantity'));
  }

  get hasGiftWrap() {
    return this.getAttribute('data-has-gift-wrap') === "true";
  }

  get giftWrapVariantId() {
    return parseInt(this.getAttribute('data-gift-wrap-variant-id'));
  }
}

customElements.define('cart-quantity', CartQuantity);

// "Remove kit, keep product" — event delegation for dynamically rendered drawer content
document.addEventListener('click', async function (e) {
  const btn = e.target.closest('.js-kit-dekit[data-drawer]');
  if (!btn) return;
  e.preventDefault();

  const parentKey = btn.dataset.parentKey;
  const kitGroupId = btn.dataset.kitGroupId;

  try {
    const cart = await fetch('/cart.js').then(r => r.json());

    const kitChildren = cart.items.filter(item => {
      const props = item.properties || {};
      return props._kit_group_id === kitGroupId && props._kit_is_child === 'true';
    });

    const mainItem = cart.items.find(item => item.key === parentKey);
    if (!mainItem) return;

    const cleanedProps = Object.fromEntries(
      Object.entries(mainItem.properties || {}).filter(([k]) => !k.startsWith('_kit_'))
    );

    if (kitChildren.length > 0) {
      const updates = Object.fromEntries(kitChildren.map(c => [c.key, 0]));
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: parentKey, quantity: mainItem.quantity, properties: cleanedProps, sections: 'cart-drawer' })
    });

    const parsed = await response.json();
    window.eventBus.emit('update:cart:drawer', parsed);

    const countIndicator = document.querySelector('[data-cart-count-indicator]');
    if (countIndicator) {
      fetch('/cart.js').then(r => r.json()).then(c => window.wetheme.updateCartCount(c));
    }
  } catch (err) {
    console.error('Failed to remove kit from drawer:', err);
  }
});

/******/ })()
;